import { useState } from "react";
import { Box, Text, Button, Textarea, TextInput, Checkbox, Label } from "@okshaun/components";
import { css } from "@styled-system/css";
import type { MemoryEntry } from "@/lib/api";
import { Wrap } from "@/styled-system/jsx";

interface MemoryRowProps {
  entry: MemoryEntry;
  highlight?: string;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete: (id: string) => Promise<void>;
  onUpdate: (id: string, text: string, tags: string[]) => Promise<void>;
}

const markClass = css({
  bg: "[rgba(255,210,50,0.28)]",
  color: "inherit",
  borderRadius: "1",
});

function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? <mark key={i} className={markClass}>{part}</mark> : part
      )}
    </>
  );
}

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleString();
}

export function MemoryRow({ entry, highlight = "", selected = false, onToggleSelect, onDelete, onUpdate }: MemoryRowProps) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editText, setEditText] = useState(entry.content);
  const [editTagsRaw, setEditTagsRaw] = useState(
    entry.tags.filter((t) => !t.startsWith("tier:")).join(", ")
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const newTags = editTagsRaw.split(",").map((t) => t.trim()).filter(Boolean);
      await onUpdate(entry.id, editText, newTags);
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(entry.id);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditText(entry.content);
    setEditTagsRaw(entry.tags.filter((t) => !t.startsWith("tier:")).join(", "));
    setConfirmDelete(false);
  };

  const displayTags = entry.tags.filter((t) => !t.startsWith("tier:"));

  return (
    <Box
      as="article"
      display="flex"
      flexDir="column"
      gap="8"
      bg="surface"
      borderBottom="default"
      py="12"
      px="16"
    >
      {/* Meta row */}
      <Box display="flex" alignItems="center" gap="8" flexWrap="wrap">
        <Label htmlFor={`check-${entry.id}`} textStyle="mono.xs" display="flex" alignItems="center" gap="4" ml="-4" color="text.subtlest" title={formatDate(entry.created_at)}>
          <Checkbox
            name={`check-${entry.id}`}
            checked={selected}
            onChange={() => onToggleSelect?.(entry.id)}
          />
          {entry.id} | {formatRelativeTime(entry.created_at)}{entry.source && ` | ${entry.source}`}
        </Label>
        {!editing && (
          <Box display="flex" gap="4" ml="auto">
            {!confirmDelete && (
              <Button size="sm" variant="subtle" type="button" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {confirmDelete ? (
              <Box display="flex" gap="4" alignItems="center">
                <Button
                  size="sm"
                  variant="primary"
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  loading={deleting}
                >
                  Confirm
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Button size="sm" variant="subtle" type="button" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Content: view vs edit */}
      {editing ? (
        <Box display="flex" flexDir="column" gap="8">
          <Textarea
            name="edit-content"
            value={editText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setEditText(e.target.value); setSaveError(null); }}
            rows={5}
            fontFamily="mono"
            fontSize="12"
          />
          <Box display="flex" alignItems="center" gap="8">
            <Text textStyle="mono.xs" color="text.subtlest" flexShrink="0">
              Tags:
            </Text>
            <TextInput
              name="edit-tags"
              value={editTagsRaw}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditTagsRaw(e.target.value)}
              placeholder="tag1, tag2, tag3"
              flex="[1]"
              fontFamily="mono"
              fontSize="12"
            />
          </Box>
          {saveError && (
            <Text textStyle="mono.xs" color="text.danger">{saveError}</Text>
          )}
          <Box display="flex" gap="8">
            <Button size="sm" type="button" onClick={handleSave} disabled={saving || !editText.trim()} loading={saving}>
              Save
            </Button>
            <Button size="sm" variant="subtle" type="button" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Text as="p" textStyle="mono.xs" color="text" maxW="full">
          <HighlightedText text={entry.content} query={highlight} />
        </Text>
      )}

      {!editing && (
        <Wrap pt="4">
          {displayTags.map((tag) => (
            <Box key={tag} textStyle="mono.xs" px="6" rounded="999" bg="bg.neutral">
              <span>#<HighlightedText text={tag} query={highlight} /></span>
            </Box>
          ))}
        </Wrap>
      )}
    </Box>
  );
}
